import React, { useEffect, useState, useRef} from 'react';

import { styled} from '@mui/material/styles';
import { useTheme, useMediaQuery, Button, Chip, Grid, Link, Divider, Stack, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Typography, TextareaAutosize, Tooltip, Checkbox, IconButton, Popper, Paper} from '@mui/material';
import EditFineDialog from './EditFineDialog';
import { Email, Drafts } from '@mui/icons-material';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs'; 


//components import
import DocumentViewer from './DocumentViewer';
import FilerFine from '../dataObjects/FilerFine'
import AlertInfo from '../../../dataObjects/AlertInfo';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import { NoticeTypeEnums } from '../dataObjects/NoticeTypeEnums';
import { WaiverStatusTypeEnums } from '../dataObjects/WaiverStatusTypeEnums';
import EmailEvent from '../dataObjects/EmalEvent';
import MoreFollowUpNotices from './MoreFollowUpNotices';

interface FineListProps {
    fineIssues?: FilerFine[];
    updatePayload: () => void;
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: '#fafafa',
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));


const FilerFinesTable = (props: FineListProps): JSX.Element => {

    const theme = useTheme();
    const isXL = useMediaQuery(theme.breakpoints.down('xl'));

    useEffect(() => {
        setFineIssues(props.fineIssues);
    }, [props.fineIssues]);

    const Headers = ['Filer', 'Audit Period', '1st E-Notice', 'Resp.Due Day', '1st Mail Sent?', '2nd E-Notice', '2nd Mail Sent?', 'Days Late', 'Penalty', 'Date Paid', 'Waiver Status', 'Action', 'Notes'];

    const [isEditFineDialogOpen, setEditFineDialogOpen] = useState(false);
    const [isDocumentViewerOpen, setDocumentViewerOpen] = useState(false);
    const [isEmailNotice, setIsEmailNotice] = useState(false);
    const [isPaperNotice, setIsPaperNotice] = useState(false);
    const [noticeTypeId, setNoticeTypeId] = useState(0);
    const [currentNoticeUrl, setCurrentNoticeUrl] = useState('');
    const [currentFineInfo, setCurrentFineInfo] = useState<FilerFine | null>(null);
    const [currentWaiver, setCurrentWaiver] = useState<FilerFine | null>(null);
    const [fineIssues, setFineIssues] = useState<FilerFine[]>();

    const [isFollowUpDialogOpen, setFollowUpDialogOpen] = useState(false);

    /*Update the resolved date*/

    const [currentFineInfoNewResolvedDate, setCurrentFineInfoNewResolvedDate] = useState("");
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const openUpdateResolvedDateDialog = (event: React.MouseEvent<HTMLElement>, fine: FilerFine) => {
        setCurrentFineInfo(fine);
        setCurrentFineInfoNewResolvedDate(fine.resolvedDate || "");
        if (fine.daysLate > 0) {
            //only opens the dialog if there is a fine
            setAnchorEl(anchorEl ? null : event.currentTarget);
        }
    };

    const isUpdateResolutionDateDialogOpen = Boolean(anchorEl);

    const handleDateChange = (date: Dayjs | null) => {
        let newDate = date?.toDate().toLocaleDateString() || "";
        setCurrentFineInfoNewResolvedDate(newDate);
    };

    const updateResolvedDate = () => {

        if (currentFineInfo && currentFineInfoNewResolvedDate) {
            fetch(`/FineTracker/UpdateResolutionDate?fineIssueId=${currentFineInfo.fineIssueId}&resolutionDate=${currentFineInfoNewResolvedDate}`)
                .then(result => result.json())
                .then(result => {
                    if (result.isError) {
                        let info = {
                            type: 'error',
                            title: 'Error: Update Resolution Date',
                            errorMessage: result.errorMessage,
                            secondaryErrorMessage: result.secondaryErrorMessage
                        };
                        setAlertInfo(info);
                        setAlertInfoDialog(true);
                    }
                    else {
                        setCurrentFineInfo(null);
                        setCurrentFineInfoNewResolvedDate("");
                        setAnchorEl(null);
                        props.updatePayload();
                    }
                  }
                )
        }
        else {
            let info = {
                type: 'error',
                title: 'Error: Update Resolution Date',
                errorMessage: "New Resolution Date is NULL",
                secondaryErrorMessage: ""
            };
            setAlertInfo(info);
            setAlertInfoDialog(true);
        }
    }

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    const displaySecondEmailDeliveryStatus = (fine: FilerFine) => {
        let emailEvents = fine.secondNoticeEmailEvents;
        let deliveredEvents = emailEvents.filter((event: EmailEvent) => event.eventType === "delivered");
        let failedEvents = emailEvents.filter((event: EmailEvent) => event.eventType === "failed");

        //if there is a delivered event, show success icon as the email is delivered even it may failed before the delivered event
        if (deliveredEvents.length > 0) {
            return <Email color="success" sx={{ fontSize: "18px" }} />
        }

        if (failedEvents.length > 0) {
            let event = failedEvents[0];
            return (
                <Tooltip title={`${event.recipientEmailAddress}: ${event.deliveryStatusMessage}`} placement="top">
                    <Email color="error" sx={{ fontSize: "18px" }} />
                </Tooltip>
            )
        }

        return("");         
    }

    const openEditFineDialog = (currentFine: FilerFine) => {
        setCurrentFineInfo(currentFine);
        setEditFineDialogOpen(true);
        
    }

    const closeEditFineDialog = (reload: boolean = false) => {
        setEditFineDialogOpen(false);
        setCurrentFineInfo(null);
        if (reload) {
            props.updatePayload();
        }
    };

    const openDocumentViewer = (currentFine: FilerFine, url: string, isEmail: boolean, isPaper: boolean, typeId: number = 0) => {
        //url ='https://ocpf2.blob.core.windows.net/pdf/filers/17408/17408-audit-1-050125.html'
        setDocumentViewerOpen(true);
        setCurrentFineInfo(currentFine);
        setCurrentNoticeUrl(url);
        setIsEmailNotice(isEmail);
        setIsPaperNotice(isPaper);
        setNoticeTypeId(typeId);
    }

    const openDocumentViewerForWaiver = (waiver: FilerFine, url: string) => {
        setDocumentViewerOpen(true);
        setCurrentWaiver(waiver);
    }

    const closeDocumentViewer = (reload: boolean = false) => {
        setDocumentViewerOpen(false);
        setCurrentFineInfo(null);
        setCurrentWaiver(null);
        setCurrentNoticeUrl('');
        setIsEmailNotice(false);
        setIsPaperNotice(false);
        setNoticeTypeId(0);
        if (reload) {
            props.updatePayload();
        }
    };

    const updatePaperNotice = (currentFine: FilerFine, typeId:number, event: React.ChangeEvent<HTMLInputElement>) => {

        let isSent = event.target.checked;
        let id = currentFine.fineIssueId;
        fetch(`/fineTracker/UpdatePaperNoticeStatus?id=${id}&isSent=${isSent}&noticeTypeId=${typeId}`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: UpdatePaperNoticeStatus',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setCurrentFineInfo(null);
                    props.updatePayload();
                }
            }
          )
    }

    const noteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const updateNote = (event: any, currentFine: FilerFine) => {
        let note = event.target.value;
        if (noteTimeoutRef.current) {
            clearTimeout(noteTimeoutRef.current);
        }
        noteTimeoutRef.current = setTimeout(() => {
            saveNote(note, currentFine);
        }, 1000);
    }

    const saveNote = (note: string, currentFine: FilerFine,) => {

        fetch(`/FineTracker/SaveNote?fineIssueId=${currentFine.fineIssueId}&cpfId=${currentFine.cpfId}&auditPeriodId=${currentFine.auditPeriodId}&note=${encodeURIComponent(note)}`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: SaveNote',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setCurrentFineInfo(null);
                }
            }
         )
    }


    //follow up dialog
    const openFollowUpDialog = (currentFine: FilerFine) => {
        setCurrentFineInfo(currentFine);
        setFollowUpDialogOpen(true);
    }

    const closeFollowUpDialog = () => {
        setFollowUpDialogOpen(false);
        setCurrentFineInfo(null);
    };

    return (
        <Grid container marginTop={5} display={'flex'} justifyContent="center">
            <Grid item xs={isXL?11.5:10.5} marginBottom={2}>
                <Divider sx={{height:"1px"}} />
            </Grid>
            <Grid item xs={isXL?11.5:10.5} display={'flex'} justifyContent="flex-end" marginBottom={2}>
                <Stack direction="row" spacing={0.5}>
                    <IconButton aria-label="export to collection" size="small" color="success">
                        <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 15 15"><path fill="currentColor" d="M4.5 6.995H4v1h.5zm6 1h.5v-1h-.5zm-6 2.505H4v.5h.5zm6 0v.5h.5v-.5zm-6-6.503H4v1h.5zm6 1h.5v-1h-.5zm3-1.497h.5v-.207l-.146-.147zm-3-3l.354-.354L10.707 0H10.5zm-6 7.495h6v-1h-6zM4.5 11h6v-1h-6zm0-6.003h6v-1h-6zm8 9.003h-10v1h10zM2 13.5v-12H1v12zm11-10v10h1v-10zM2.5 1h8V0h-8zm7.646-.146l3 3l.708-.708l-3-3zM2.5 14a.5.5 0 0 1-.5-.5H1A1.5 1.5 0 0 0 2.5 15zm10 1a1.5 1.5 0 0 0 1.5-1.5h-1a.5.5 0 0 1-.5.5zM2 1.5a.5.5 0 0 1 .5-.5V0A1.5 1.5 0 0 0 1 1.5zm2 3v6h1v-6zm3 0v6h1v-6zm3 0v6h1v-6z" /></svg>
                        <Typography variant={'body2'} marginLeft={0.2}>
                            Collection List
                        </Typography>
                    </IconButton>
                    <IconButton aria-label="export to collection" size="small" color="success">
                        <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 15 15"><path fill="currentColor" d="M4.5 6.995H4v1h.5zm6 1h.5v-1h-.5zm-6 2.505H4v.5h.5zm6 0v.5h.5v-.5zm-6-6.503H4v1h.5zm6 1h.5v-1h-.5zm3-1.497h.5v-.207l-.146-.147zm-3-3l.354-.354L10.707 0H10.5zm-6 7.495h6v-1h-6zM4.5 11h6v-1h-6zm0-6.003h6v-1h-6zm8 9.003h-10v1h10zM2 13.5v-12H1v12zm11-10v10h1v-10zM2.5 1h8V0h-8zm7.646-.146l3 3l.708-.708l-3-3zM2.5 14a.5.5 0 0 1-.5-.5H1A1.5 1.5 0 0 0 2.5 15zm10 1a1.5 1.5 0 0 0 1.5-1.5h-1a.5.5 0 0 1-.5.5zM2 1.5a.5.5 0 0 1 .5-.5V0A1.5 1.5 0 0 0 1 1.5zm2 3v6h1v-6zm3 0v6h1v-6zm3 0v6h1v-6z" /></svg>
                        <Typography variant={'body2'} marginLeft={0.2}>
                            AG List
                        </Typography>
                    </IconButton>
                </Stack>
            </Grid>
            <Grid item xs={isXL?11.5:10.5}>
                <TableContainer>
                    <Table aria-label="Filer Fine Table" size="small">
                        <TableHead>
                            <TableRow>
                                {
                                    Headers.map((header, index) => (
                                        <TableCell key={index}>
                                            <Typography variant={'subtitle2'} fontWeight={600}>
                                                {header}
                                            </Typography>
                                        </TableCell>
                                    ))
                                }
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {fineIssues?.map((fine: FilerFine) => (
                                <StyledTableRow key={fine.fineIssueId}>
                                    <TableCell sx={{width:"15%"}} >
                                        <Typography variant={'body2'}>
                                            <Link href={`/Filers?q=${fine.cpfId}`} target="_blank">{fine.cpfId}</Link> {fine.fullName}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant={'body2'}>
                                            {fine.auditPeriodId}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.2} alignItems="center">
                                            <Email color="success" sx={{fontSize: "18px"}} />
                                            <Typography variant={'body2'} component={Link} onClick={() => openDocumentViewer(fine, fine.auditLetterUrl,true,false)} >
                                                {fine.initialNoticeElectronicDate}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant={'body2'}>
                                            {fine.resolutionDeadline}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.2} alignItems="center">
                                            <Checkbox defaultChecked={fine.initialNoticePaperDate ? true:false} onChange={(event) => updatePaperNotice(fine, NoticeTypeEnums.Initial, event)} size="small" />
                                            <Typography variant={'body2'} component={Link} onClick={() => openDocumentViewer(fine, '', false, true, NoticeTypeEnums.Initial)}>
                                                View
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    {
                                        fine.resolvedDate ?
                                            <TableCell colSpan={2}>
                                                <Chip label={`Issue resolved on ${fine.resolvedDate}`} color="success" variant="outlined" onClick={(event) => openUpdateResolvedDateDialog(event, fine)} ></Chip>
                                                <Popper
                                                    open={isUpdateResolutionDateDialogOpen}
                                                    anchorEl={anchorEl}
                                                    placement={"right-end"}
                                                >
                                                    <Paper sx={{ p: 2 }}>
                                                        <Grid item xs={12}>
                                                            <Stack spacing={2} alignItems="center" display="flex" justifyContent="flex-start">
                                                                <Typography variant="subtitle1" fontWeight={550}>
                                                                    Update the resolved date to recalculate the fine:
                                                                </Typography>
                                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                                    <DatePicker onChange={handleDateChange} sx={{ minWidth: 100}} defaultValue={currentFineInfo?.resolvedDate ? dayjs(currentFineInfo.resolvedDate) : null} label="Resolved Date" />
                                                                </LocalizationProvider>
                                                                <Stack direction="row" spacing={1}>
                                                                    <Button size="small" color="warning" onClick={() => setAnchorEl(null)}>Cancel</Button>
                                                                    <Button size="small" onClick={updateResolvedDate} >Update</Button>
                                                                </Stack>
                                                            </Stack>
                                                        </Grid>
                                                    </Paper>
                                                </Popper>
                                            </TableCell>
                                         :
                                            <>
                                                <TableCell>
                                                    {
                                                        fine.secondNoticeElectronicDate && fine.secondNoticeEmailEvents && fine.secondNoticeEmailEvents.length>0 ?
                                                            <Stack direction="row" spacing={0.2} alignItems="center">
                                                                {displaySecondEmailDeliveryStatus(fine)}
                                                                <Typography variant={'body2'} component={Link} onClick={() => openDocumentViewer(fine, fine.secondNoticeFilerDocumentUrl, true, false, NoticeTypeEnums.Second)}>
                                                                    {fine.secondNoticeElectronicDate}
                                                                </Typography>
                                                            </Stack> :
                                                            <Stack direction="row" spacing={0.2} alignItems="center" onClick={() => openDocumentViewer(fine, '', true, false, NoticeTypeEnums.Second)}>
                                                                <Drafts color={fine.daysLate<0?'disabled':'warning'} sx={{ fontSize: "18px" }} />
                                                                <Typography variant={'body2'} component={fine.daysLate < 0 ? Button : Link} disabled={fine.daysLate < 0} >
                                                                    {fine.secondNoticeElectronicDate}
                                                                </Typography>
                                                            </Stack>
                                                    }

                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={0.2} alignItems="center">
                                                        <Checkbox disabled={fine.daysLate < 0} defaultChecked={fine.secondNoticePaperDate ? true : false} size="small" onChange={(event) => updatePaperNotice(fine, NoticeTypeEnums.Second, event)} />
                                                        <Typography variant={'body2'} component={fine.daysLate < 0 ? Button : Link} disabled={fine.daysLate < 0} target="_blank" onClick={() => openDocumentViewer(fine, '', false, true, NoticeTypeEnums.Second)}>
                                                            View
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                            </>
                                    }
                                    {
                                        fine.daysLate <= 0 ?
                                            <>
                                                <TableCell sx={{ textAlign: "center!important" }}>-</TableCell>
                                                <TableCell colSpan={4}></TableCell>
                                            </>
                                            
                                            :
                                            <>
                                                <TableCell>
                                                    <Typography variant={'body2'}>
                                                        {fine.daysLate}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ textAlign: "right!important" }} >
                                                    <Typography variant={'body2'} fontWeight={550} color="error">
                                                        {
                                                            fine.reducedFineAmount ? `${fine.reducedFineAmount}(reduced from ${fine.unmodifiedFineAmount})` : fine.modifiedFineAmount
                                                        }
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant={'body2'}>
                                                        {
                                                            fine.fineDatePaid ? `${fine.fineAmountPaid} paid on ${fine.fineDatePaid}` : ''
                                                        }
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {
                                                        fine.waiverStatus === WaiverStatusTypeEnums.Pending &&
                                                        <Chip label={`Pending`} color="warning" variant="outlined" onClick={() => openDocumentViewerForWaiver(fine, '')}></Chip>
                                                    }
                                                    {
                                                        fine.waiverStatus === WaiverStatusTypeEnums.Processed &&
                                                        <Chip label={fine.waiverDecisionTypeDescription} color="primary" variant="outlined" onClick={() => openDocumentViewerForWaiver(fine, '')}></Chip>
                                                    }
                                                </TableCell>
                                                <TableCell sx={{ width: "10%" }}>
                                                    <Stack spacing={1}>
                                                        {
                                                            fine.resolvedDate &&
                                                            <>
                                                                <Button variant="outlined" size="small" onClick={() => openEditFineDialog(fine)}>Update Fine</Button>
                                                                <Button variant="outlined" size="small" color="warning" onClick={() => openFollowUpDialog(fine)} >Fine Notices</Button>
                                                            </>
                                                        }
                                                        
                                                        {
                                                            /**
                                                            !fine.resolvedDate && fine.daysLate >= 20 &&
                                                            <Button variant="outlined" size="small" color="error" onClick={() => openFollowUpDialog(fine)} >Late Notices</Button>
                                                            **/
                                                        }
                                                    </Stack>
                                                </TableCell>
                                            </>
                                    }
                                    <TableCell>
                                        <TextareaAutosize
                                            aria-label="filer notes"
                                            minRows={2}
                                            defaultValue={fine.note}
                                            style={{ width: 100 }}
                                            onChange={(event:any)=>updateNote(event,fine)}
                                        />
                                    </TableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
            {isEditFineDialogOpen && currentFineInfo && (
                <EditFineDialog
                    isEditFineDialogOpen={isEditFineDialogOpen}
                    closeEditFineDialog={closeEditFineDialog}
                    currentFineInfo={currentFineInfo}
                />
            )}
            <DocumentViewer isDocumentViewerOpen={isDocumentViewerOpen} closeDocumentViewer={closeDocumentViewer} currentFineInfo={currentFineInfo} currentWaiverInfo={currentWaiver} currentNoticeUrl={currentNoticeUrl} isEmailNotice={isEmailNotice} isPaperNotice={isPaperNotice} noticeTypeId={noticeTypeId} ></DocumentViewer>
            <MoreFollowUpNotices isFollowUpDialogOpen={isFollowUpDialogOpen} closeFollowUpDialog={closeFollowUpDialog} currentFineInfo={currentFineInfo} ></MoreFollowUpNotices>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </Grid>
    );
};

export default FilerFinesTable;